import { memo } from "react";
import { X } from "@phosphor-icons/react";

export interface SupplierSuggestion {
  supplier: {
    id: number;
    name: string;
    email?: string;
    company?: {
      id: number;
      name: string;
    };
    contact?: {
      id: number;
      name: string;
    };
  };
  ticketCount: number;
}

export interface SupplierSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  suppliers: SupplierSuggestion[];
  label: string | null;
  ticketIds?: number[]; // Made optional since it's not used in the component
  onApplySupplier: (supplier: any) => Promise<void>;
  isUpdating: boolean;
}

/**
 * SupplierSuggestionModal - Modal for suggesting frequently-used suppliers
 *
 * Features:
 * - Shows top 3 suppliers based on frequency for a specific label
 * - Displays ticket count for each suggestion
 * - One-click application to multiple tickets
 * - Loading state during update
 */
const SupplierSuggestionModalComponent = ({
  isOpen,
  onClose,
  suppliers,
  label,
  onApplySupplier,
  isUpdating,
}: SupplierSuggestionModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="animate-in fade-in fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4 duration-200">
      <div className="border-neutral-primary/10 animate-in zoom-in-95 slide-in-from-bottom-4 w-full max-w-md overflow-hidden rounded-xl border bg-white shadow-xl duration-300 ease-out">
        <div className="bg-moss-primary/10 border-moss-primary/20 flex items-center justify-between border-b p-4">
          <h3 className="text-moss-primary text-lg font-semibold">
            Leverancier suggestie
          </h3>
          <button
            onClick={onClose}
            className="text-neutral-secondary-mid hover:text-moss-primary transition-colors"
            disabled={isUpdating}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          <p className="text-neutral-primary mb-4 text-sm">
            Voor <strong>{label}</strong> tickets worden deze leveranciers vaak
            gebruikt:
          </p>
          <div className="mb-4 space-y-2">
            {suppliers.slice(0, 3).map((suggestion) => (
              <button
                key={suggestion.supplier.id}
                onClick={() => onApplySupplier(suggestion.supplier)}
                disabled={isUpdating}
                className="border-moss-primary/20 hover:bg-moss-primary/5 w-full rounded-md border p-3 text-left transition-colors disabled:opacity-50"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-neutral-primary font-medium">
                      {suggestion.supplier.company?.name ||
                        suggestion.supplier.name}
                    </div>
                    {suggestion.supplier.contact?.name && (
                      <div className="text-neutral-secondary-mid text-xs">
                        {suggestion.supplier.contact.name}
                      </div>
                    )}
                  </div>
                  <span className="bg-moss-primary rounded-full px-2 py-1 text-xs text-white">
                    {suggestion.ticketCount}x gebruikt
                  </span>
                </div>
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={isUpdating}
              className="border-neutral-primary/20 hover:bg-neutral-primary/5 flex-1 rounded-md border px-3 py-2 text-sm transition-colors disabled:opacity-50"
            >
              Later kiezen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SupplierSuggestionModal = memo(SupplierSuggestionModalComponent);
